import { telegramMiniAppLogin } from "@kupikupi/api";
import { API_BASE_URL } from "@kupikupi/brand";
import { getCloudStorageItem, setCloudStorageItem } from "@telegram-apps/sdk-react";
import { type ReactNode, useEffect, useRef } from "react";
import { markAuthReady, readAuthToken, setAuthToken, useAuth } from "../lib/auth";
import { getInviteCode, getRawInitData } from "../lib/telegram-auth";

/** TG CloudStorage key — survives across the user's Telegram devices. */
const CLOUD_TOKEN_KEY = "kk_auth_token";

/** Best-effort read from Telegram CloudStorage. Returns null off-TG / on error. */
async function readCloudToken(): Promise<string | null> {
  try {
    if (!getCloudStorageItem.isAvailable()) return null;
    const v = await getCloudStorageItem(CLOUD_TOKEN_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Best-effort write to Telegram CloudStorage. Silent on error / off-TG. */
async function writeCloudToken(token: string): Promise<void> {
  try {
    if (!setCloudStorageItem.isAvailable()) return;
    await setCloudStorageItem(CLOUD_TOKEN_KEY, token);
  } catch {
    // CloudStorage unavailable (old client / browser preview) — localStorage
    // already holds the token, so this is a pure best-effort upgrade.
  }
}

/**
 * Silent Telegram auth shell (#226 Phase 1).
 *
 * On boot, if no JWT is already in localStorage:
 *   1. try Telegram CloudStorage (cross-device persistence) and adopt it;
 *   2. otherwise grab the WebView `initData`, exchange it for a JWT via
 *      `POST /sys/thirdLogin/telegram/initData`, persist to both localStorage
 *      (for `useAuthToken`) and CloudStorage.
 *
 * Runs once per mount and is idempotent (the `attempted` ref guards React 19
 * StrictMode's double-invoke). Children are held behind a tiny root loading
 * shell until auth is ready, so routes never misclassify boot-in-progress as
 * definitely signed-out.
 *
 * Outside Telegram (browser preview, tests) `getRawInitData()` returns null and
 * this is a no-op — the email/password fallback on `/login` stays the path.
 */
export function AuthBootstrap({ children }: { children: ReactNode }) {
  // A plain ref guard (not the reactive `useAuthToken`) drives the once-only
  // semantics. Two deliberate choices, both fixing real bugs:
  //   - Empty deps + ref guard: the bootstrap must run exactly once for the
  //     app's lifetime. Depending on `token`/a mutation object would let the
  //     mutation's own re-render re-fire or *cancel* the in-flight effect and
  //     silently drop a just-resolved JWT.
  //   - No `cancelled` flag: persisting the token to localStorage is correct
  //     even if this component unmounts mid-flight (the next mount reads it
  //     back via `useAuthToken`), so there is nothing to abort.
  // The ref is set AFTER the synchronous skip-checks but the async body owns
  // its own idempotency, so StrictMode's mount→unmount→mount still results in a
  // single real exchange.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Already authenticated (e.g. token set by a prior mount / the /login
    // page) — read the live value, not a stale render closure.
    if (readAuthToken()) {
      markAuthReady();
      return;
    }

    void (async () => {
      // 1. Cross-device token from Telegram CloudStorage.
      const cloud = await readCloudToken();
      if (readAuthToken()) {
        markAuthReady();
        return;
      } // a concurrent path already set it
      if (cloud) {
        setAuthToken(cloud);
        return;
      }

      // 2. Fresh silent exchange from initData.
      const initData = getRawInitData();
      if (!initData) {
        markAuthReady();
        return;
      } // not in a TG WebView → leave logged-out

      try {
        const inviteCode = getInviteCode();
        const result = await telegramMiniAppLogin(
          { baseUrl: API_BASE_URL },
          inviteCode ? { initData, inviteCode } : { initData },
        );
        const jwt = result.xaccessToken;
        if (jwt) {
          setAuthToken(jwt);
          void writeCloudToken(jwt);
        }
      } catch {
        // Verification / network failure: stay logged-out so the /login page's
        // email fallback remains the path. Swallowed so a backend hiccup never
        // white-screens the Mini App on boot. (Retry is a manual /login press;
        // a transient failure should not silently lock the user out forever —
        // see #226 Phase-3 remainder: add a bounded auto-retry.)
      } finally {
        markAuthReady();
      }
    })();
  }, []);

  const { ready } = useAuth();

  if (!ready) {
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          color: "var(--color-text-muted)",
          fontSize: 12,
        }}
      >
        …
      </div>
    );
  }

  return <>{children}</>;
}
